import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SandboxBadge, { SANDBOX_DIALOG_SX } from "components/common/SandboxBadge";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { getAbdmBridge, getAbdmIdHolders, updateAbdmBridgeUrl } from "api/abdmBridge";
import ListPagination, { ALL_ROWS } from "components/common/ListPagination";
import useListParams, { toPage, toPageSize } from "hooks/useListParams";
import ServiceLookupCard from "./ServiceLookupCard";
import {
  bridgeAlerts,
  bridgeChangeOutcome,
  callbackExample,
  checkBridgeUrl,
  serviceTypeLabel,
} from "utils/abdmBridge";
import { abdmServiceHolders } from "utils/abdmServiceStatus";

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("en-IN");
};

const mono = { fontFamily: "monospace", wordBreak: "break-all" };
const buttonSx = { textTransform: "none", fontWeight: 600, borderRadius: "10px" };

/**
 * DRCFO's ABDM software bridge as the gateway holds it: GET /admin/abdm/bridge
 * (§3.2.7 Find services by bridge id) gives its callback URL, whether ABDM
 * marks it active or blocklisted, and every Service ID registered under it.
 * On the sandbox, "Change callback URL" sends PATCH /admin/abdm/bridge/url
 * (§3.2.4 Update bridge URL) once the admin types the Bridge ID to confirm. In
 * production the page is read-only: ABDM's Integration Support Team changes
 * the URL there (ABDM FAQ #3), and abdm.backend refuses it. "Check a Service
 * ID" (ServiceLookupCard) asks §3.2.6 about any id an admin types, as the
 * doctor profile's "Verify ABDM IDs" does for the doctor's own ids. The
 * services table names, beside each Service ID, the doctor profiles holding it
 * as their HIP, HIU or HFR ID (GET /admin/abdm/id-holders, every profile with
 * an id, matched here), each a link to the profile.
 */
const AbdmBridge = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [changing, setChanging] = useState(false);
  const [outcome, setOutcome] = useState("");
  // The doctor profiles with an id, read beside the bridge. A read that fails
  // keeps the last one and says so in the services table.
  const [holders, setHolders] = useState({ doctors: [], error: "" });
  // Only the newest read may fill the page.
  const latest = useRef(0);

  const load = useCallback(async () => {
    const call = ++latest.current;
    setLoading(true);
    const [response, profiles] = await Promise.all([getAbdmBridge(), getAbdmIdHolders()]);
    if (call !== latest.current) return;
    if (response?.status === 200 && response.data?.data?.bridge) {
      setData(response.data.data);
      setError("");
    } else {
      setError(response?.data?.message || "Could not read the bridge from ABDM.");
    }
    const doctors = profiles?.data?.data?.doctors;
    if (profiles?.status === 200 && Array.isArray(doctors)) {
      setHolders({ doctors, error: "" });
    } else {
      setHolders((current) => ({
        ...current,
        error: profiles?.data?.message || "The doctor profiles could not be read.",
      }));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changed = (change) => {
    // A read that started before the change would show the old URL again.
    latest.current += 1;
    setLoading(false);
    setChanging(false);
    setOutcome(
      bridgeChangeOutcome({
        url: change.url,
        // abdm.backend reads the old URL just before the change and leaves it
        // out when that read failed. The page's own last read is no stand-in:
        // another developer on the bridge may have changed the URL since.
        previousUrl: change.previousUrl,
      })
    );
    setData((current) => ({
      ...current,
      bridge: { ...current.bridge, url: change.url },
    }));
    toast.success("Callback URL changed");
  };

  if (!data) {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      );
    }
    return (
      <Box p={3}>
        <Typography variant="h5" fontWeight={700} mb={3}>
          ABDM Bridge
        </Typography>
        <Alert severity="error" data-testid="abdm-bridge-error">
          {error}
        </Alert>
        <Button variant="contained" onClick={load} sx={{ ...buttonSx, mt: 2 }}>
          Try again
        </Button>
      </Box>
    );
  }

  const { bridge } = data;
  const services = Array.isArray(data.services) ? data.services : [];
  const production = data.gateway === "production";
  const example = callbackExample(bridge.url);
  const readAt = formatTime(data.checkedAt);

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography variant="h5" fontWeight={700}>
              ABDM Bridge
            </Typography>
            {data.gateway &&
              (production ? (
                <Chip
                  label="Production"
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 600 }}
                  data-testid="abdm-bridge-gateway"
                />
              ) : (
                <SandboxBadge keepInProd>
                  <Chip
                    label="Test"
                    color="info"
                    size="small"
                    sx={{ fontWeight: 600 }}
                    data-testid="abdm-bridge-gateway"
                  />
                </SandboxBadge>
              ))}
          </Box>
          <Typography fontSize="13px" color="#6b7280" mt={0.5}>
            DRCFO's software bridge as the ABDM gateway holds it
            {readAt ? `, read ${readAt}` : ""}.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
          }
          disabled={loading}
          onClick={load}
          sx={buttonSx}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} data-testid="abdm-bridge-error">
          <AlertTitle>The refresh failed</AlertTitle>
          {error} The details below are from the last read.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: "18px", p: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              mb={2}
            >
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="h6" fontWeight={700}>
                  Bridge
                </Typography>
                {typeof bridge.active === "boolean" && (
                  <Chip
                    size="small"
                    label={bridge.active ? "Active" : "Inactive"}
                    color={bridge.active ? "success" : "error"}
                    sx={{ fontWeight: 600 }}
                  />
                )}
                {bridge.blocklisted === true && (
                  <Chip size="small" label="Blocklisted" color="error" sx={{ fontWeight: 600 }} />
                )}
              </Box>
              {data.canUpdateUrl && (
                <SandboxBadge keepInProd>
                  <Button
                    variant="contained"
                    startIcon={<EditOutlinedIcon />}
                    disabled={!bridge.id}
                    onClick={() => setChanging(true)}
                    sx={buttonSx}
                  >
                    Change callback URL
                  </Button>
                </SandboxBadge>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <InfoItem label="Bridge ID (client id)" value={bridge.id} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoItem label="Name" value={bridge.name} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoItem label="Entity" value={bridge.entity} />
              </Grid>
              <Grid item xs={12}>
                <InfoItem
                  label="Callback URL"
                  value={bridge.url}
                  monospace
                  testId="abdm-bridge-url"
                />
              </Grid>
            </Grid>
            {example && (
              <Typography fontSize="13px" color="#6b7280" data-testid="abdm-bridge-example">
                ABDM adds /api/v3/&lt;path&gt; to this URL: patient discovery, for one, goes to{" "}
                <Box component="span" sx={mono}>
                  {example}
                </Box>
              </Typography>
            )}
            {bridgeAlerts(bridge).map((alert) => (
              <Alert
                key={alert.key}
                severity={alert.severity}
                sx={{ mt: 2 }}
                data-testid={`abdm-bridge-alert-${alert.key}`}
              >
                <AlertTitle>{alert.title}</AlertTitle>
                {alert.message}
              </Alert>
            ))}
            {!data.canUpdateUrl && (
              <Typography
                fontSize="13px"
                color="#6b7280"
                mt={2}
                data-testid="abdm-bridge-readonly"
              >
                {production
                  ? "The production callback URL is changed by the ABDM Integration Support Team (ABDM FAQ #3), not from here."
                  : "This server cannot change the callback URL."}
              </Typography>
            )}
            {outcome && (
              <Alert
                severity="success"
                sx={{ mt: 2 }}
                onClose={() => setOutcome("")}
                data-testid="abdm-bridge-outcome"
              >
                {outcome}
              </Alert>
            )}
          </Card>
        </Grid>

        <Grid item xs={12}>
          <ServiceLookupCard services={services} />
        </Grid>

        <Grid item xs={12}>
          <ServicesCard services={services} holders={holders} />
        </Grid>
      </Grid>

      {changing && (
        <ChangeUrlDialog
          bridge={bridge}
          onClose={() => setChanging(false)}
          onChanged={changed}
          onUncertain={load}
        />
      )}
    </Box>
  );
};

/**
 * The §3.2.4 change. Nothing is sent until the URL passes the backends' checks
 * and the admin has typed the Bridge ID; a refusal stays in the dialog. A
 * timeout is not a refusal: whoever gave up waiting (abdm.backend, drcfo.backend
 * or the proxy), ABDM may still apply the change, so the page reads the bridge
 * again (`onUncertain`) and the dialog says the change may have gone through.
 */
const ChangeUrlDialog = ({ bridge, onClose, onChanged, onUncertain }) => {
  const [value, setValue] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);

  const check = checkBridgeUrl(value, bridge.url);
  const typed = value.trim() !== "";
  const confirmed = confirm.trim() === bridge.id;
  const example = check.error ? "" : callbackExample(check.url);

  const save = async () => {
    setSaving(true);
    setFailure(null);
    const response = await updateAbdmBridgeUrl(check.url);
    if (response?.status === 200) {
      const change = response.data?.data || {};
      onChanged({ ...change, url: change.url || check.url });
      return;
    }
    setSaving(false);
    if (!response || response.status === 504) {
      setFailure({
        severity: "warning",
        message: `${response?.data?.message || "No answer came back."} The change may still have been applied, so the page reads the bridge again: if the current callback URL above still shows the old one, refresh in a minute before trying again.`,
      });
      onUncertain();
      return;
    }
    setFailure({
      severity: "error",
      message: response.data?.message || "Could not change the callback URL.",
    });
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={saving ? undefined : onClose}>
      <SandboxBadge keepInProd sx={SANDBOX_DIALOG_SX}>
        <DialogTitle sx={{ fontWeight: 600, bgcolor: "#F9F9F9" }}>
          Change callback URL
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 3 }}>
            Every ABDM test callback for bridge {bridge.id} (discovery, linking, consent and
            health-information requests) goes to this URL, for every developer and environment
            sharing the bridge. ABDM may keep calling the old URL for a few minutes.
          </Alert>
          <InfoItem label="Current callback URL" value={bridge.url} monospace />
          <TextField
            id="abdm-bridge-new-url"
            label="New callback URL"
            placeholder="https://"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            error={typed && Boolean(check.error)}
            helperText={
              typed && check.error
                ? check.error
                : "The base address: ABDM adds /api/v3/<path> to it."
            }
            disabled={saving}
            fullWidth
            size="small"
            autoFocus
            inputProps={{ "data-testid": "abdm-bridge-new-url", spellCheck: false }}
          />
          {example && (
            <Typography
              fontSize="13px"
              color="#6b7280"
              mt={1}
              data-testid="abdm-bridge-url-preview"
            >
              ABDM will call, for example,{" "}
              <Box component="span" sx={mono}>
                {example}
              </Box>
            </Typography>
          )}
          {check.warning && (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="abdm-bridge-url-warning">
              {check.warning}
            </Alert>
          )}
          <TextField
            id="abdm-bridge-confirm"
            label="Type the Bridge ID (client id) to confirm"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            helperText={`Type ${bridge.id} exactly.`}
            disabled={saving}
            fullWidth
            size="small"
            sx={{ mt: 3 }}
            inputProps={{
              "data-testid": "abdm-bridge-confirm",
              autoComplete: "off",
              spellCheck: false,
            }}
          />
          {failure && (
            <Alert
              severity={failure.severity}
              sx={{ mt: 2 }}
              data-testid="abdm-bridge-change-error"
            >
              {failure.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={saving} sx={buttonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            disabled={Boolean(check.error) || !confirmed || saving}
            onClick={save}
            sx={buttonSx}
          >
            {saving ? "Changing..." : "Change URL"}
          </Button>
        </DialogActions>
      </SandboxBadge>
    </Dialog>
  );
};

const SERVICE_LIST_DEFAULTS = { q: "", status: "", type: "", page: "1", size: "25" };
const SERVICE_PAGE_SIZES = [10, 25, 50, 100];

const STATUS_OPTIONS = [
  { value: "", label: "Any" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

/** What a holder is to the service, beside the name: its ids, role and status. */
const holderNote = ({ doctor, heldAs }) =>
  [
    heldAs.join(", "),
    doctor.role?.type && doctor.role.type !== "DOCTOR" ? doctor.role.type : "",
    doctor.status && doctor.status !== "ACTIVE" ? String(doctor.status).toLowerCase() : "",
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * The bridge's Service IDs, searched, filtered and paged here: ABDM sends the
 * whole list in one answer. The search, filters, page and page size live in
 * the URL (hooks/useListParams), like the doctors list; `size=all` shows
 * every row. Beside each service, the doctor profiles holding its id
 * (`holders.doctors`, matched by `abdmServiceHolders`); the search matches
 * their names too.
 */
const ServicesCard = ({ services, holders }) => {
  const navigate = useNavigate();
  const [params, update] = useListParams(SERVICE_LIST_DEFAULTS);
  const topRef = useRef(null);
  const inactive = services.filter((service) => service.active === false).length;
  const types = useMemo(
    () => [...new Set(services.flatMap((service) => service.types || []))].sort(),
    [services]
  );
  const entries = useMemo(
    () =>
      services.map((service) => ({
        service,
        held: abdmServiceHolders(holders.doctors, service.id),
      })),
    [services, holders.doctors]
  );

  const query = params.q.trim().toLowerCase();
  const status = ["active", "inactive"].includes(params.status) ? params.status : "";
  const type = types.includes(params.type) ? params.type : "";
  const pageSize =
    params.size === "all"
      ? ALL_ROWS
      : toPageSize(params.size, SERVICE_PAGE_SIZES, Number(SERVICE_LIST_DEFAULTS.size));
  const filtered = Boolean(query || status || type);

  const matching = entries.filter(({ service, held }) => {
    if (query) {
      const names = held.map(({ doctor }) => doctor.name || "").join("\n");
      const text = `${service.id || ""}\n${service.name || ""}\n${names}`.toLowerCase();
      if (!text.includes(query)) return false;
    }
    if (status && service.active !== (status === "active")) return false;
    if (type && !(service.types || []).includes(type)) return false;
    return true;
  });
  const showAll = pageSize === ALL_ROWS;
  const pageCount = showAll ? 1 : Math.max(1, Math.ceil(matching.length / pageSize));
  // A page past the end (the filters narrowed the list) shows the last one.
  const page = Math.min(toPage(params.page), pageCount);
  const rows = showAll ? matching : matching.slice((page - 1) * pageSize, page * pageSize);

  const setFilter = (key) => (event) => update({ [key]: event.target.value, page: 1 });
  const clearFilters = () => update({ q: "", status: "", type: "", page: 1 });
  const goToPage = (value) => update({ page: value });
  const summary = filtered
    ? ({ range }) => `${range} (${services.length.toLocaleString("en-IN")} in all)`
    : undefined;
  const noun = filtered ? "matching services" : "services";

  return (
    <Card sx={{ borderRadius: "18px", p: 3 }}>
      <Box
        ref={topRef}
        display="flex"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
        mb={2}
        sx={{ scrollMarginTop: "1rem" }}
      >
        <Typography variant="h6" fontWeight={700}>
          Services
        </Typography>
        <Chip
          size="small"
          label={services.length}
          sx={{ fontWeight: 600 }}
          data-testid="abdm-bridge-service-count"
        />
        {inactive > 0 && (
          <Chip
            size="small"
            color="warning"
            variant={status === "inactive" ? "filled" : "outlined"}
            label={`${inactive} inactive`}
            title={status === "inactive" ? "Show every service" : "Show the inactive services"}
            aria-pressed={status === "inactive"}
            onClick={() => update({ status: status === "inactive" ? "" : "inactive", page: 1 })}
            sx={{ fontWeight: 600 }}
            data-testid="abdm-bridge-inactive-filter"
          />
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Typography fontSize="13px" color="#6b7280" mb={2}>
        The facilities registered under this bridge. A doctor's HIP ID must be one of these
        Service IDs for ABHA record sharing to reach DRCFO. The Doctors column names the
        profiles holding each id as their HIP, HIU or HFR ID.
      </Typography>
      {holders.error && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="abdm-bridge-holders-error">
          {holders.error} The Doctors column{" "}
          {holders.doctors.length > 0 ? "shows the last read." : "is empty."}
        </Alert>
      )}
      {services.length === 0 ? (
        <Typography data-testid="abdm-bridge-no-services">
          ABDM lists no services under this bridge.
        </Typography>
      ) : (
        <>
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} mb={1}>
            <TextField
              id="abdm-services-search"
              size="small"
              label="Search services"
              placeholder="Service ID, name or doctor"
              value={params.q}
              onChange={setFilter("q")}
              sx={{ minWidth: 260 }}
            />
            <TextField
              select
              id="abdm-services-status"
              size="small"
              label="Status"
              value={status}
              onChange={setFilter("status")}
              sx={{ minWidth: 130 }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value || "any"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              id="abdm-services-type"
              size="small"
              label="Type"
              value={type}
              onChange={setFilter("type")}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">Any</MenuItem>
              {types.map((value) => (
                <MenuItem key={value} value={value}>
                  {serviceTypeLabel(value)}
                </MenuItem>
              ))}
            </TextField>
            <Button size="small" onClick={clearFilters} disabled={!filtered} sx={buttonSx}>
              Clear filters
            </Button>
          </Box>
          <ListPagination
            testId="abdm-bridge-services-pager-top"
            page={page}
            pageSize={pageSize}
            total={matching.length}
            noun={noun}
            summary={summary}
            pageSizeOptions={[...SERVICE_PAGE_SIZES, ALL_ROWS]}
            onPageChange={goToPage}
            onPageSizeChange={(size) =>
              update({ size: size === ALL_ROWS ? "all" : size, page: 1 })
            }
          />
          {matching.length === 0 ? (
            <Box py={3} textAlign="center" data-testid="abdm-bridge-no-match">
              <Typography mb={1}>No service matches these filters.</Typography>
              <Button variant="outlined" onClick={clearFilters} sx={buttonSx}>
                Clear filters
              </Button>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                <Table size="small" data-testid="abdm-bridge-services">
                  <TableHead sx={{ bgcolor: "#F5F5F5" }}>
                    <TableRow>
                      <TableCell>
                        <b>Service ID (facility / HIP id)</b>
                      </TableCell>
                      <TableCell>
                        <b>Name</b>
                      </TableCell>
                      <TableCell>
                        <b>Types</b>
                      </TableCell>
                      <TableCell>
                        <b>Status</b>
                      </TableCell>
                      <TableCell>
                        <b>Doctors</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(({ service, held }, index) => (
                      <TableRow key={`${index}-${service.id}`} hover>
                        <TableCell sx={mono}>{service.id || "-"}</TableCell>
                        <TableCell>{service.name || "-"}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {(service.types || []).map((value, at) => (
                              <Chip
                                key={`${at}-${value}`}
                                size="small"
                                variant="outlined"
                                label={serviceTypeLabel(value)}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {typeof service.active === "boolean" ? (
                            <Chip
                              size="small"
                              label={service.active ? "Active" : "Inactive"}
                              color={service.active ? "success" : "warning"}
                              sx={{ fontWeight: 600 }}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell data-testid="abdm-service-holders">
                          {held.length === 0 ? (
                            "-"
                          ) : (
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              {held.map((holder) => (
                                <Box
                                  key={holder.doctor._id}
                                  display="flex"
                                  alignItems="baseline"
                                  gap={1}
                                  flexWrap="wrap"
                                >
                                  <Link
                                    component="button"
                                    type="button"
                                    underline="hover"
                                    fontWeight={600}
                                    fontSize="14px"
                                    sx={{ textAlign: "left" }}
                                    onClick={() => navigate(`/doctors/${holder.doctor._id}`)}
                                  >
                                    {holder.doctor.name || holder.doctor._id}
                                  </Link>
                                  <Typography component="span" fontSize="12px" color="#6b7280">
                                    {holderNote(holder)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <ListPagination
                testId="abdm-bridge-services-pager-bottom"
                compact
                page={page}
                pageSize={pageSize}
                total={matching.length}
                noun={noun}
                summary={summary}
                onPageChange={(value) => {
                  goToPage(value);
                  topRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
                }}
              />
            </>
          )}
        </>
      )}
    </Card>
  );
};

const InfoItem = ({ label, value, monospace = false, testId }) => (
  <Box mb={2.5}>
    <Typography fontSize="13px" color="#6b7280" mb={0.5}>
      {label}
    </Typography>
    <Typography
      fontWeight={600}
      color="#111827"
      sx={monospace ? mono : undefined}
      data-testid={testId}
    >
      {value || value === 0 ? value : "-"}
    </Typography>
  </Box>
);

export default AbdmBridge;
